import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Loader2, Mic, Square, Upload } from "lucide-react";

import { Modal } from "../../components/ui/modal";
import { useToast } from "../../components/ui/toast";
import { cn } from "../../lib/utils";
import { AudioClipper } from "./audio-clipper";
import { decodeAudio } from "./audio";
import { useVoiceRecorder } from "./use-voice-recorder";
import { CLIP_SECONDS, CLONE_GENDERS, CLONE_LANGUAGES, type ClonedVoice } from "./types";

interface CloneVoiceModalProps {
  existing?: ClonedVoice | null;
  onClose: () => void;
  onSubmit: (input: { name: string; language: string; gender: string; clip: Blob }) => Promise<void>;
}

export function CloneVoiceModal({ existing, onClose, onSubmit }: CloneVoiceModalProps) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recorder = useVoiceRecorder();

  const [buffer, setBuffer] = useState<AudioBuffer | null>(null);
  const [clip, setClip] = useState<Blob | null>(null);
  const [decoding, setDecoding] = useState(false);
  const [name, setName] = useState(existing?.local_voice_name ?? "");
  const [language, setLanguage] = useState(existing?.language || "hi");
  const [gender, setGender] = useState(existing?.gender || "Female");
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const isReclone = !!existing;

  const loadAudio = useCallback(
    async (source: Blob) => {
      setDecoding(true);
      try {
        setBuffer(await decodeAudio(source));
      } catch {
        toast.danger("Could not read that audio", "Try a wav, mp3, m4a or webm file.");
      } finally {
        setDecoding(false);
      }
    },
    [toast],
  );

  const onPickFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) await loadAudio(file);
  };

  const toggleRecording = async () => {
    if (recorder.recording) {
      const recorded = await recorder.stop();
      if (recorded) await loadAudio(recorded);
      return;
    }
    setBuffer(null);
    setClip(null);
    await recorder.start();
  };

  const onClipChange = useCallback((next: Blob) => setClip(next), []);

  const canSave = !!clip && !!name.trim() && !decoding && consent && !saving;

  const submit = async () => {
    if (!clip || !canSave) return;
    setSaving(true);
    try {
      await onSubmit({ name: name.trim(), language, gender, clip });
      onClose();
    } catch (error) {
      toast.danger(
        isReclone ? "Could not update the voice" : "Could not clone the voice",
        (error as Error)?.message,
      );
    } finally {
      setSaving(false);
    }
  };

  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!recorder.stream || !canvasRef.current || !recorder.recording) return;
    
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const analyser = audioCtx.createAnalyser();
    const source = audioCtx.createMediaStreamSource(recorder.stream);
    source.connect(analyser);
    analyser.fftSize = 256;
    
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d");
    if (!canvasCtx) return;
    
    let animationId: number;
    
    const draw = () => {
      animationId = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = (canvas.width / bufferLength) * 2.5;
      let barHeight;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        barHeight = dataArray[i] / 2;
        canvasCtx.fillStyle = `hsl(262, 83%, ${Math.max(50, 80 - barHeight/4)}%)`;
        canvasCtx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        x += barWidth + 1;
      }
    };
    
    draw();
    
    return () => {
      cancelAnimationFrame(animationId);
      audioCtx.close().catch(() => {});
    };
  }, [recorder.stream, recorder.recording]);

  return (
    <Modal
      title={isReclone ? `Re-record ${existing?.local_voice_name}` : "Clone a voice"}
      subtitle={
        isReclone
          ? "The new sample replaces the old one. Agents using this voice keep working."
          : `Provide voice details, confirm consent, and record or upload a sample.`
      }
      onClose={onClose}
      width="max-w-5xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink hover:bg-soft mr-auto"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={submit}
            disabled={!canSave}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50 ml-auto transition-colors shadow-sm"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isReclone ? "Update voice" : "Clone voice"}
          </button>
        </>
      }
    >
      <div className="grid md:grid-cols-2 gap-8 items-stretch pt-2">
         {/* LEFT COLUMN: Details & Consent */}
         <div className="flex flex-col gap-6">
            {!isReclone && (
               <div className="space-y-4 bg-canvas/40 p-5 rounded-xl border border-line">
                  <h3 className="text-sm font-bold text-ink uppercase tracking-wider mb-2 text-ink-3 border-b border-line pb-2">1. Voice Details</h3>
                  <div className="space-y-4">
                     <label className="block text-sm">
                       <span className="mb-1.5 block font-medium text-ink">Name</span>
                       <input
                         value={name}
                         onChange={(event) => setName(event.target.value)}
                         placeholder="e.g. Riya"
                         maxLength={120}
                         className="w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand shadow-sm transition-colors"
                       />
                     </label>
                     <div className="grid grid-cols-2 gap-4">
                        <label className="block text-sm">
                          <span className="mb-1.5 block font-medium text-ink">Language</span>
                          <select
                            value={language}
                            onChange={(event) => setLanguage(event.target.value)}
                            className="w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand shadow-sm transition-colors"
                          >
                            {CLONE_LANGUAGES.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                        <label className="block text-sm">
                          <span className="mb-1.5 block font-medium text-ink">Gender</span>
                          <select
                            value={gender}
                            onChange={(event) => setGender(event.target.value)}
                            className="w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand shadow-sm transition-colors"
                          >
                            {CLONE_GENDERS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                     </div>
                  </div>
               </div>
            )}
            
            <div className={cn(
              "space-y-4 bg-canvas/40 p-5 rounded-xl border transition-colors mt-auto",
              consent ? "border-brand bg-brand/5" : "border-line"
            )}>
               <h3 className="text-sm font-bold uppercase tracking-wider text-ink-3 border-b border-line pb-2">
                 {isReclone ? "1." : "2."} Legal Consent
               </h3>
               <label className="flex cursor-pointer items-start gap-4 transition-colors group">
                 <div className={cn(
                   "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors", 
                   consent ? "border-brand bg-brand" : "border-line bg-white group-hover:border-ink-3"
                 )}>
                   {consent && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                 </div>
                 <input type="checkbox" className="hidden" checked={consent} onChange={() => setConsent(!consent)} />
                 <span className="text-sm text-ink font-medium leading-relaxed group-hover:text-ink">
                   I confirm I have the rights and permission to clone this voice, that the speaker consented to its use in automated calls, and that I will not use it to impersonate anyone.
                 </span>
               </label>
            </div>
         </div>

         {/* RIGHT COLUMN: Audio Sample */}
         <div className="flex flex-col space-y-4">
            <h3 className="text-sm font-bold text-ink-3 uppercase tracking-wider border-b border-line pb-2">
              {isReclone ? "2." : "3."} Audio Sample
            </h3>
            
            <div className="relative flex-1 flex flex-col justify-center">
               <div className="flex-1 flex flex-col space-y-4 transition-opacity">
                  {!buffer && (
                     <div className={cn(
                       "rounded-xl border p-6 flex flex-col items-center text-center transition-colors h-full justify-center",
                       recorder.recording ? "border-brand/30 bg-brand/5" : "border-line bg-canvas/50"
                     )}>
                        <div className="mb-6">
                          <h3 className="text-xs font-semibold text-ink-3 uppercase tracking-wider mb-3">Suggested Script</h3>
                          <p className={cn(
                            "text-[15px] leading-relaxed font-medium italic max-w-sm transition-colors",
                            recorder.recording ? "text-ink" : "text-ink-2"
                          )}>
                             "Hello! I'm {name || 'your virtual assistant'}. I'm calling to quickly follow up on your recent request, do you have a minute to chat?"
                          </p>
                        </div>

                        <div className="flex flex-col items-center w-full max-w-sm">
                          <div className="h-16 w-full mb-6 flex items-center justify-center bg-white/60 rounded-lg border border-line relative overflow-hidden shadow-inner">
                            {recorder.recording ? (
                              <canvas ref={canvasRef} width={400} height={60} className="w-full h-full" />
                            ) : null}
                          </div>

                          <div className="flex flex-col items-center gap-3 h-16">
                             <button
                               type="button"
                               onClick={toggleRecording}
                               disabled={!recorder.supported}
                               className={cn(
                                 "inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium transition-all shadow-sm",
                                 recorder.recording
                                   ? "bg-danger text-white hover:bg-danger/90 animate-pulse shadow-danger/20"
                                   : "bg-white border border-line text-ink hover:bg-soft",
                                 (!recorder.supported) && "cursor-not-allowed opacity-50",
                               )}
                             >
                               {recorder.recording ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-4 w-4" />}
                               {recorder.recording ? "Stop Recording" : "Start Recording"}
                             </button>
                             
                             {recorder.recording && (
                               <p className="text-sm font-medium">
                                 <span className={cn(recorder.elapsedMs >= 10000 ? "text-success" : "text-ink-2")}>
                                   {Math.floor(recorder.elapsedMs / 1000)}s
                                 </span>
                                 <span className="text-ink-3"> / 10s recommended</span>
                               </p>
                             )}
                             
                             {recorder.error && <p className="text-sm text-danger mt-1">{recorder.error}</p>}
                          </div>
                        </div>
                     </div>
                  )}

                  {!buffer && (
                     <>
                       <div className="flex items-center justify-center gap-4 pt-1">
                          <div className="h-px bg-line flex-1" />
                          <span className="text-xs text-ink-3 uppercase font-medium">OR</span>
                          <div className="h-px bg-line flex-1" />
                       </div>

                       <div className="flex justify-center">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={recorder.recording}
                            className="inline-flex items-center gap-2 rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink hover:bg-soft disabled:opacity-50 transition-colors bg-white shadow-sm"
                          >
                            <Upload className="h-4 w-4" />
                            Upload an audio file
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="audio/*"
                            className="hidden"
                            onChange={onPickFile}
                          />
                       </div>
                     </>
                  )}

                  {decoding && (
                     <div className="rounded-xl border border-line bg-canvas/50 p-12 flex flex-col items-center justify-center flex-1 h-[300px]">
                       <Loader2 className="h-6 w-6 animate-spin text-ink-3 mb-2" />
                       <p className="text-sm font-medium text-ink-2">Reading audio file…</p>
                     </div>
                  )}

                  {buffer && !recorder.recording && (
                     <div className="space-y-4 rounded-xl border border-line bg-canvas/40 p-5 flex-1 flex flex-col justify-center min-h-[300px]">
                       <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-bold text-ink uppercase tracking-wider text-ink-3 border-b border-line pb-1">Adjust Clip</h3>
                          <button 
                            type="button"
                            onClick={() => { setBuffer(null); setClip(null); }}
                            className="text-xs font-medium text-brand hover:underline"
                          >
                            Discard & start over
                          </button>
                       </div>
                       <AudioClipper buffer={buffer} onChange={onClipChange} />
                     </div>
                  )}
               </div>
            </div>
         </div>
      </div>
    </Modal>
  );
}
